// <s-spin-wheel> — floating button that opens the prize wheel. Picks a slice by weight, spins the disc so it lands
// under the pointer, then reveals its discount code. A slice without a code is "come back tomorrow": the visitor can
// spin again from the next calendar day. A won prize is remembered for N days (one spin per visitor).
const STORAGE_KEY = "petslife-spin-wheel";

const dayKey = (time) => {
  const d = new Date(time);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

class SSpinWheel extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.dialog = this.querySelector("[data-wheel-dialog]");
    this.disc = this.querySelector("[data-wheel-disc]");
    if (!this.dialog || !this.disc) return;

    try {
      this.prizes = JSON.parse(this.querySelector("[data-wheel-prizes]")?.textContent || "[]");
    } catch {
      this.prizes = [];
    }
    this.slice = this.prizes.length ? 360 / this.prizes.length : 60;

    this.querySelector("[data-wheel-open]")?.addEventListener("click", () => this.#open(), { signal });
    this.querySelector("[data-wheel-close]")?.addEventListener("click", () => this.dialog.close(), { signal });
    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog || event.target.matches("[data-wheel-backdrop]")) this.dialog.close();
    }, { signal });
    this.querySelector("[data-wheel-spin]")?.addEventListener("click", () => this.#spin(), { signal });

    const copy = this.querySelector("[data-wheel-copy]");
    copy?.addEventListener("click", () => {
      const code = this.querySelector("[data-wheel-code]")?.textContent || "";
      navigator.clipboard?.writeText(code).then(() => { copy.textContent = copy.dataset.copiedLabel; });
    }, { signal });

    if (this.dataset.preview === "true") {
      this.#open();
      return;
    }
    const saved = this.#saved();
    if (saved !== null) this.#showResult(saved, false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #open() {
    if (!this.dialog.open) this.dialog.showModal();
  }

  #isRetry(prize) {
    return !prize || !prize.code;
  }

  #saved() {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return null;
      const prize = this.prizes[data.index];
      if (!prize) return null;
      if (this.#isRetry(prize)) return dayKey(data.at) === dayKey(Date.now()) ? data.index : null;
      const days = Math.max(1, parseInt(this.dataset.days, 10) || 30);
      if (Date.now() - data.at > days * 86400000) return null;
      return data.index;
    } catch {
      return null;
    }
  }

  #pick() {
    const total = this.prizes.reduce((sum, p) => sum + Math.max(0, Number(p.weight) || 0), 0);
    if (total <= 0) return 0;
    const random = crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
    let roll = random * total;
    for (let i = 0; i < this.prizes.length; i++) {
      roll -= Math.max(0, Number(this.prizes[i].weight) || 0);
      if (roll < 0) return i;
    }
    return this.prizes.length - 1;
  }

  #restingAngle(index) {
    return 360 - (index * this.slice + this.slice / 2);
  }

  #spin() {
    const button = this.querySelector("[data-wheel-spin]");
    if (!this.prizes.length || button.disabled) return;
    button.disabled = true;
    const index = this.#pick();
    const jitter = (Math.random() - 0.5) * (this.slice - 14);
    this.disc.style.transform = `rotate(${360 * 6 + this.#restingAngle(index) + jitter}deg)`;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ index, at: Date.now() })); } catch {}
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.setTimeout(() => this.#showResult(index, true), reduced ? 50 : 4600);
  }

  #toggle(selector, show) {
    this.querySelectorAll(selector).forEach((el) => el.classList.toggle("hidden", !show));
  }

  #showResult(index, focus) {
    const prize = this.prizes[index];
    if (!prize) return;
    if (!this.disc.style.transform) this.disc.style.transform = `rotate(${this.#restingAngle(index)}deg)`;
    const retry = this.#isRetry(prize);

    this.querySelector("[data-wheel-actions]")?.classList.add("hidden");
    this.#toggle("[data-wheel-won-line]", !retry);
    this.#toggle("[data-wheel-code-only]", !retry);
    this.#toggle("[data-wheel-retry-line]", retry);
    this.#toggle("[data-wheel-retry-body]", retry);

    const shop = this.querySelector("[data-wheel-shop]");
    if (!retry) {
      this.querySelector("[data-wheel-prize]").textContent = prize.label;
      this.querySelector("[data-wheel-code]").textContent = prize.code;
      if (shop) shop.href = `/discount/${encodeURIComponent(prize.code)}?redirect=${encodeURIComponent(shop.getAttribute("href"))}`;
    }

    const result = this.querySelector("[data-wheel-result]");
    result?.classList.remove("hidden");
    result?.classList.add("flex");
    if (focus) (retry ? shop : this.querySelector("[data-wheel-copy]"))?.focus();
  }
}

if (!customElements.get("s-spin-wheel")) customElements.define("s-spin-wheel", SSpinWheel);
